"use client";

import { useEffect, useRef, useState, useCallback } from "react";

interface CountUpProps {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  decimals?: number;
  className?: string;
}

export default function CountUp({
  end,
  prefix = "",
  suffix = "",
  duration = 2000,
  decimals = 0,
  className,
}: CountUpProps) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const reducedMotionRef = useRef(false);

  const animate = useCallback(() => {
    if (reducedMotionRef.current) {
      setCount(end);
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  /* eslint-disable react-hooks/set-state-in-effect -- syncing with external systems (matchMedia, IntersectionObserver) */
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const userPref = document.documentElement.getAttribute("data-reduce-motion") === "true";
    const shouldReduce = mediaQuery.matches || userPref;
    reducedMotionRef.current = shouldReduce;

    if (shouldReduce) {
      setCount(end);
      setHasStarted(true);
      return;
    }

    const el = ref.current;
    if (!el) {
      setCount(end);
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([_entry]) => {
        if (_entry?.isIntersecting) {
          setHasStarted(true);
          observer.unobserve(_entry.target);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    const handleMotionChange = () => {
      const userPrefNow = document.documentElement.getAttribute("data-reduce-motion") === "true";
      const reduce = mediaQuery.matches || userPrefNow;
      reducedMotionRef.current = reduce;
      if (reduce) {
        setCount(end);
        setHasStarted(true);
        observer.disconnect();
      }
    };

    mediaQuery.addEventListener("change", handleMotionChange);

    const mutationObserver = new MutationObserver(handleMotionChange);
    mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-reduce-motion"],
    });

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleMotionChange);
      mutationObserver.disconnect();
    };
  }, [end]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!hasStarted) return;
    return animate(); // eslint-disable-line react-hooks/set-state-in-effect -- animation driven by requestAnimationFrame callbacks
  }, [hasStarted, animate]);

  const formatNumber = (num: number) => {
    if (decimals > 0 || end % 1 !== 0) {
      return num.toFixed(decimals > 0 ? decimals : 1);
    }
    return num.toLocaleString();
  };

  return (
    <span ref={ref} className={className} suppressHydrationWarning>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  );
}
